<template>
  <div
    id="position"
    class="position obs-el"
    data-attr="position"
  >
    Расположение
  </div>
  <div class="address">{{ sight.address }}</div>
  <div
    v-map-view-tracker="analyticsBase"
    class="map-container"
  >
    <NativeYandexMap
      v-if="sight.latitude && sight.longitude"
      :latitude="sight.latitude"
      :longitude="sight.longitude"
      :zoom="15"
      @map-action="onMapAction"
    />
  </div>
  <div class="map-btn">
    <UiButtonV3
      :variant="UiButtonVariant.SECONDARY"
      :color-mode="UiButtonColorMode.NEUTRAL"
      class="copy"
      text="Копировать"
      data-test="map-copy"
      is-full-width
      @clicked="copy"
    >
      <template #icon>
        <IconBase name="copy-20-icon-old" />
      </template>
    </UiButtonV3>

    <div class="button-container">
      <UiButtonV3
        ref="pathBtn"
        :variant="UiButtonVariant.SECONDARY"
        :color-mode="UiButtonColorMode.NEUTRAL"
        text="Маршрут"
        data-test="map-route"
        is-full-width
        @clicked="onRouteClick"
      >
        <template #icon><IconBase name="route-20-icon" /></template>
      </UiButtonV3>
      <div
        v-show="isOpenPopup"
        ref="popup"
        :key="tooltipPosition"
        :class="['path-content', tooltipPosition]"
      >
        <ul>
          <li
            v-for="popupEl in popupContent"
            :key="popupEl.icon"
            @click.stop="goTo(popupEl.url, popupEl.service)"
          >
            <IconBase :name="popupEl.icon" /> {{ popupEl.title }}
          </li>
        </ul>
      </div>
    </div>
  </div>

  <UiToast
    :open="toastOpen"
    @close="toastOpen = false"
  >
    <template #icon><IconBase name="copy-ok" /></template>
    <template #content>{{ toastTitle }}</template>
  </UiToast>
</template>

<script setup lang="ts">
  import { UiToast } from '@experience/core-toast-ui';
  import { UiButtonV3, UiButtonColorMode, UiButtonVariant } from '@experience/vue3-kit';
  import { onClickOutside } from '@vueuse/core';
  import { computed, ref, useTemplateRef } from 'vue';

  import { trackEvent, useViewTracker } from '@/shared/lib/analytics/compat';
  import type { SightModalCity, SightModalSight } from '@/shared/types/sight-modal';
  import IconBase from '@/shared/ui/IconBase.vue';
  import NativeYandexMap from '@/shared/ui/sights/NativeYandexMap.vue';

  type TooltipPosition = 'position-top' | 'position-bottom';

  const POPUP_HEIGHT_THRESHOLD = 235;

  const props = withDefaults(
    defineProps<{
      sight: SightModalSight;
      city: SightModalCity;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      analyticsBase: () => ({}),
    },
  );

  const vMapViewTracker = useViewTracker('around:sights:sight-popup:map.view');

  const popupContent = computed(() => {
    const { latitude, longitude } = props.sight;
    const latitudeParam = String(latitude ?? '');
    const longitudeParam = String(longitude ?? '');

    return [
      {
        title: 'Яндекс Карты',
        url: `https://maps.yandex.ru/?text=${latitudeParam},${longitudeParam}&z=17`,
        icon: 'yandex-map-icon-24',
        service: 'yandex',
      },
      {
        title: '2ГИС',
        url: `https://2gis.ru/geo/${longitudeParam}%2C${latitudeParam}?m=${longitudeParam}%2C${latitudeParam}%2F17`,
        icon: '2gis-icon-24',
        service: '2gis',
      },
      {
        title: 'Google maps',
        url: `https://www.google.com/maps/place/${latitudeParam},${longitudeParam}`,
        icon: 'google-map-icon-24',
        service: 'google',
      },
    ];
  });

  const toastTitle = ref('');
  const toastOpen = ref(false);
  const isOpenPopup = ref(false);
  const popupRef = useTemplateRef('popup');
  const pathBtnRef = useTemplateRef('pathBtn');
  const tooltipPosition = ref<TooltipPosition>('position-bottom');

  const fullAddress = computed(() => {
    const cityName = props.city.name_ru;
    const address = props.sight.address;

    if (!address) return cityName;

    return cityName ? `${cityName}, ${address}` : address;
  });

  const updateTooltipPosition = () => {
    const modalElement = document.querySelector('.ui-modal');

    if (!modalElement || !pathBtnRef.value) return;

    const modalRect = modalElement.getBoundingClientRect();
    const btnRect = pathBtnRef.value.$el.getBoundingClientRect();
    const spaceBelow = modalRect.height + modalRect.top - btnRect.top;

    tooltipPosition.value = spaceBelow > POPUP_HEIGHT_THRESHOLD ? 'position-bottom' : 'position-top';
  };

  // setTimeout нужен, чтобы клик по кнопке при открытой модалке закрывал её, а не открывал заново из-за click outside
  const togglePopup = (forceState?: boolean) => {
    if (typeof forceState === 'boolean') {
      isOpenPopup.value = forceState;
      updateTooltipPosition();

      return;
    }

    setTimeout(() => {
      isOpenPopup.value = !isOpenPopup.value;
    }, 1);
  };

  onClickOutside(popupRef, () => {
    if (isOpenPopup.value) {
      togglePopup();
    }
  });

  const copy = async () => {
    void trackEvent('around:sights:sight-popup:copy-location.click', { ...props.analyticsBase });

    try {
      await navigator.clipboard.writeText(fullAddress.value);
      toastTitle.value = 'Адрес скопирован';
    } catch {
      toastTitle.value = 'Ошибка при копировании';
    } finally {
      toastOpen.value = true;
    }
  };

  const onRouteClick = () => {
    void trackEvent('around:sights:sight-popup:route.click', { ...props.analyticsBase });
    togglePopup(true);
  };

  const onMapAction = (action: string) => {
    void trackEvent('around:sights:sight-popup:map.click', { ...props.analyticsBase, action });
  };

  const goTo = (link: string, service: string) => {
    void trackEvent('around:sights:sight-popup:route-service.select', {
      ...props.analyticsBase,
      route_service: service,
    });
    window.open(link, '_blank');
    togglePopup(true);
  };
</script>

<style lang="scss" scoped>
  .position {
    margin-top: var(--alias-space-48);

    @include base-600-500-extra;
  }

  .address {
    @include base-400-400-regular;
  }

  .map-container {
    width: 100%;
    height: 343px;
    margin-top: var(--alias-space-16);
    overflow: hidden;
    border-radius: var(--alias-space-8);
  }

  .map-btn {
    display: flex;
    gap: var(--alias-space-8);
    margin-top: var(--alias-space-8);

    @media screen and (width <= 360px) {
      flex-direction: column;
    }
  }

  :deep(.inline-block) {
    width: 100% !important;
    margin: 0 !important;
    border: 0 !important;
  }

  .button-container {
    position: relative;
  }

  .copy,
  .button-container {
    flex-basis: 50%;
  }

  .path-content {
    position: absolute;
    right: 0;
    z-index: 4;
    width: 100%;
    min-width: 272px;
    padding: var(--alias-space-16) var(--alias-space-24);
    background: var(--alias-color-background-default);
    border-radius: var(--alias-radius-8);
    box-shadow: 0 0 18px 0 var(--global-color-transparent-black-a500);

    ul li {
      display: flex;
      gap: var(--alias-space-8);
      padding: var(--alias-space-12) 0;
      cursor: pointer;

      @include base-400-400-regular;
    }
  }

  .position-top {
    bottom: calc(100% + 8px);
  }

  .position-bottom {
    top: calc(100% + 8px);
  }
</style>
